import { Select } from "antd";
import { CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmt, niceTicks, show, signed, span, unitsOf, yearLong } from "../format";
import { chartRange, hasVT, SCENARIO_COLOURS, scenarioLabel, type Selection, type Series } from "../model";
import type { Metric, Summary } from "../types";
interface Props {
  summary: Summary;
  selection: Selection;
  metric: Metric;
  /** The selected location's numbers; null while they load. */
  series: Series | null;
  /** The location has no value of this metric at or next to it. */
  none: boolean;
  /** The location's numbers could not be loaded. */
  failed: boolean;
  dark: boolean;
  onChange: (change: Partial<Selection>) => void;
}

/** The selected location's metric through the century, one line per scenario. */
export const SeriesChart = ({
  summary,
  selection,
  metric,
  series,
  none,
  failed,
  dark,
  onChange
}: Props) => {
  const {
    points,
    sel,
    vt
  } = selection;
  const point = points[sel];
  const units = unitsOf(metric);
  const scens = summary.scenarios.map(s => s.id);
  const colour = (id: string) => SCENARIO_COLOURS[id]?.[dark ? "dark" : "light"] ?? "currentColor";
  const H = summary.horizons;
  const baseline = span(H[0]);
  const proj = H.slice(1);
  let body;
  if (!point) {
    body = <Empty text="Click the map or add a location to see how this metric changes over time under each scenario." />;
  } else if (none) {
    body = <Empty text="This metric has no value at or next to this location." />;
  } else if (failed) {
    body = <Empty text="Could not load this location." />;
  } else if (!series) {
    body = <Empty text="Loading…" />;
  } else {
    // The baseline is a 20-year average, not a period of no change, so it is drawn only as a
    // reference level across the projections.
    const base = vt === "diff" ? hasVT(metric, "diff") ? 0 : null : series[scens[0]][0];
    const values = [base, ...scens.flatMap(s => series[s].slice(1))].filter((v): v is number => v !== null);
    const [lo, hi] = chartRange(metric.id, vt, values.length ? values : [0]);
    const data = proj.map((h, j) => ({
      centre: h.centre,
      ...Object.fromEntries(scens.map(s => [s, series[s][j + 1]]))
    }));
    body = <>
        <div className="flex flex-wrap gap-x-3.5 gap-y-1 px-[18px] pt-1.5 text-xs">
          {scens.map(s => {
          const ok = series[s].slice(1).some(v => v !== null);
          return <span key={s} className={`inline-flex items-center gap-1.5 ${ok ? "" : "text-gray-400"}`}>
                <i className="inline-block h-[3px] w-3.5 rounded-sm" style={{
              background: ok ? colour(s) : "#dde3e7"
            }} />
                {scenarioLabel(summary, s)}
                {!ok && " — not available for this metric"}
              </span>;
        })}
          {base !== null && <span className="inline-flex items-center gap-1.5">
              <i className="inline-block h-0 w-3.5 border-0 border-t-2 border-dashed border-gray-500" />
              Baseline ({baseline} average)
            </span>}
        </div>
        <div className="h-[300px] px-[18px] pb-1.5 pt-3">
          <ResponsiveContainer>
            <LineChart data={data} margin={{
            top: 14,
            right: 16,
            left: 0,
            bottom: 0
          }}>
              <CartesianGrid vertical={false} stroke="currentColor" strokeOpacity={0.1} />
              <XAxis dataKey="centre" type="number" domain={[proj[0].centre - 3, proj[proj.length - 1].centre + 3]} ticks={[2020, 2030, 2040, 2050, 2060, 2070, 2080, 2090]} tick={{
              fontSize: 11
            }} stroke="currentColor" strokeOpacity={0.4} />
              <YAxis domain={[lo, hi]} ticks={niceTicks(lo, hi, 5)} tickFormatter={(v: number) => vt === "diff" ? signed(v) : fmt(v)} tick={{
              fontSize: 11
            }} width={58} stroke="currentColor" strokeOpacity={0.4} allowDataOverflow />
              <Tooltip content={({
              active,
              label
            }) => {
              const k = H.findIndex(h => h.centre === label);
              if (!active || k <= 0) return null;
              return <div className="rounded bg-surface px-2.5 py-1.5 text-xs shadow">
                      <div className="mb-0.5 font-semibold">{yearLong(H[k])}</div>
                      {scens.map(s => <div key={s} className="flex items-center gap-1.5">
                          <span className="inline-block h-2 w-2 rounded-sm" style={{
                    background: colour(s)
                  }} />
                          <b className="tabular-nums">{show(series[s][k], vt)}</b>
                          <span className="text-gray-500">{scenarioLabel(summary, s)}</span>
                        </div>)}
                      {base !== null && <div className="flex items-center gap-1.5">
                          <span className="inline-block h-2 w-2 rounded-sm bg-gray-500" />
                          <b className="tabular-nums">{show(base, vt)}</b>
                          <span className="text-gray-500">Baseline ({baseline})</span>
                        </div>}
                    </div>;
            }} />
              {selection.h > 0 && <ReferenceLine x={H[selection.h].centre} stroke="currentColor" strokeOpacity={0.5} strokeDasharray="1 3" label={{
              value: "Map",
              position: "insideTopLeft",
              fontSize: 11
            }} />}
              {base !== null && <ReferenceLine y={base} stroke="currentColor" strokeOpacity={0.7} strokeWidth={1.2} strokeDasharray="3 4" />}
              {scens.map(s => <Line key={s} dataKey={s} name={scenarioLabel(summary, s)} type="linear" stroke={colour(s)} strokeWidth={2.2} dot={{
              r: 2.2,
              strokeWidth: 0,
              fill: colour(s)
            }} activeDot={{
              r: 4
            }} connectNulls={false} isAnimationActive={false} />)}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </>;
  }
  return <section className="flex min-w-0 flex-col bg-surface" data-sentry-component="SeriesChart" data-sentry-source-file="SeriesChart.tsx">
      <div className="flex flex-wrap items-baseline gap-2.5 px-[18px] pt-2.5">
        {points.length ? <Select className="min-w-48 font-semibold" value={sel} options={points.map((p, k) => ({
        value: k,
        label: `${k + 1}. ${p.name}`
      }))} onChange={(k: number) => onChange({
        sel: k
      })} aria-label="Location shown in the chart" popupMatchSelectWidth={false} /> : <h2 className="m-0 text-sm font-semibold">Time series</h2>}
        {point && <span className="text-xs text-gray-500">
            {metric.plain_label}
            {units ? `, ${units}` : ""} · {vt === "diff" ? "change from baseline" : "raw value"}
          </span>}
      </div>
      {body}
      <p className="m-0 px-4 pb-3 text-xs leading-normal text-gray-500">
        <b className="text-gray-600">How to read the chart.</b> Each line is one SSP for the selected location; each
        point is the 20-year average around that year, so year-to-year swings are smoothed out. The dashed line is the
        baseline, the {baseline} average, for comparison, and the dotted vertical line marks the year shown on the map.
        The axis starts at zero, except for temperature and pH, where zero is an arbitrary point on the scale. Choose
        the location from the list above the chart, or click a marker on the map or a row in the table.
      </p>
    </section>;
};
const Empty = ({
  text
}: {
  text: string;
}) => <div className="px-[18px] py-[30px] text-center text-[12.5px] text-gray-500" data-sentry-component="Empty" data-sentry-source-file="SeriesChart.tsx">{text}</div>;